// frontend/src/app/pricing/page.tsx
'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/context/AuthContext';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';

export default function PricingPage() {
  const { user, getIdToken, updateUser } = useAuth();
  const router = useRouter();
  const t = useTranslations('pricing');

  const TIERS = [
    {
      id: 'free',
      name: t('freeName'),
      price: '$0',
      period: t('forever'),
      description: t('freeDesc'),
      features: [
        t('feature1Shop'),
        t('feature25Listings'),
        t('featureStorefront'),
        t('featureStripe'),
        t('featureCommission'),
      ],
      cta: t('getStarted'),
      href: '/sell/start/',
      highlight: false,
    },
    {
      id: 'pro',
      name: t('proName'),
      price: '$15',
      period: t('perMonth'),
      description: t('proDesc'),
      features: [
        t('feature3Shops'),
        t('feature100Listings'),
        t('featurePriority'),
        t('featureStripe'),
        t('featureCommission'),
      ],
      cta: t('upgradeToPro'),
      highlight: true,
    },
    {
      id: 'business',
      name: t('businessName'),
      price: '$30',
      period: t('perMonth'),
      description: t('businessDesc'),
      features: [
        t('featureUnlimitedShops'),
        t('featureUnlimitedListings'),
        t('featurePriority'),
        t('featureBulkCsv'),
        t('featureStripe'),
        t('featureCommission'),
      ],
      cta: t('upgradeToBusiness'),
      highlight: false,
    },
  ];

  const currentTier = user?.roles.includes('seller') ? user.membershipTier : null;
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Determine which card is highlighted: current plan when logged in; else default to 'pro'
  const highlightedTier = currentTier ?? 'pro';
  const TIER_ORDER: Record<string, number> = { free: 0, pro: 1, business: 2 };

  async function handleChangePlan(tierId: string) {
    if (!user) {
      router.push(`/login?next=${encodeURIComponent('/pricing/')}`);
      return;
    }
    const token = getIdToken();
    const apiUrl = process.env.NEXT_PUBLIC_API_URL;
    if (!token || !apiUrl) { setError('Not logged in.'); return; }
    setLoading(tierId);
    setError('');
    try {
      const res = await fetch(`${apiUrl}/billing/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tier: tierId }),
      });
      const j = await res.json() as { data?: { url?: string; upgraded?: boolean }; error?: string };
      if (!res.ok) {
        setError(j.error ?? 'Failed to change plan.');
        return;
      }
      if (j.data?.upgraded) {
        // Inline upgrade — no redirect needed, update local state
        updateUser({ membershipTier: tierId as 'pro' | 'business' });
        return;
      }
      if (j.data?.url) {
        window.location.href = j.data.url;
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(null);
    }
  }

  async function handleCancel() {
    const token = getIdToken();
    const apiUrl = process.env.NEXT_PUBLIC_API_URL;
    if (!token || !apiUrl) return;
    setLoading('cancel');
    setError('');
    try {
      const res = await fetch(`${apiUrl}/billing/subscribe`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({})) as { error?: string };
        setError(j.error ?? 'Failed to cancel subscription.');
        return;
      }
      // Subscription will cancel at period end; Stripe webhook will update tier
      setShowCancelConfirm(false);
      setError('');
      // Show a success state by reflecting the impending downgrade
      updateUser({ membershipTier: 'free' });
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(null);
    }
  }
  return (
    <main className="max-w-4xl mx-auto px-4 py-12 flex flex-col gap-10">
      <div className="text-center flex flex-col gap-3">
        <h1 className="text-3xl font-bold text-[#1a2e1f]">{t('title')}</h1>
        <p className="text-[#6b8c7a] text-sm max-w-lg mx-auto">
          {t('subtitle')}
        </p>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-center">{error}</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {TIERS.map(tier => {
          const isCurrent = currentTier === tier.id;
          const isHighlighted = tier.id === highlightedTier;
          const isDowngrade = currentTier != null && currentTier !== 'free' && tier.id !== currentTier && (TIER_ORDER[tier.id] ?? 0) < (TIER_ORDER[currentTier] ?? 0);
          const isUpgrade = currentTier != null && tier.id !== currentTier && (TIER_ORDER[tier.id] ?? 0) > (TIER_ORDER[currentTier] ?? 0);
          return (
            <div
              key={tier.id}
              className={`flex flex-col rounded-2xl border p-6 gap-4 ${
                isHighlighted
                  ? 'border-[#2d7a4f] bg-[#f0faf4] shadow-md'
                  : 'border-[#dde8dd] bg-white'
              }`}
            >
              {isHighlighted && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#2d7a4f] bg-[#c8e6c9] px-2 py-0.5 rounded-full self-start">
                  {isCurrent ? t('yourPlan') : t('mostPopular')}
                </span>
              )}
              <div>
                <h2 className="text-lg font-bold text-[#1a2e1f]">{tier.name}</h2>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="text-3xl font-bold text-[#1a2e1f]">{tier.price}</span>
                  <span className="text-xs text-[#6b8c7a]">{tier.period}</span>
                </div>
              </div>

              <p className="text-xs text-[#6b8c7a] leading-relaxed">{tier.description}</p>

              <ul className="flex flex-col gap-2 flex-1">
                {tier.features.map(f => (
                  <li key={f} className="flex items-start gap-2 text-xs text-[#1a2e1f]">
                    <svg className="w-3.5 h-3.5 text-[#2d7a4f] mt-0.5 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <div className="text-center text-xs font-medium text-[#2d7a4f] bg-[#e8f5e9] rounded-lg py-2">
                  {t('currentPlan')}
                </div>
              ) : isDowngrade ? (
                <button
                  type="button"
                  disabled={!!loading}
                  onClick={() => handleChangePlan(tier.id)}
                  className="text-sm font-medium text-center rounded-lg py-2.5 transition border border-stone-300 text-stone-500 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading === tier.id ? t('switching') : t('switchTo', { name: tier.name })}
                </button>
              ) : tier.id === 'free' ? (
                <Link
                  href="/sell/start/"
                  className="text-sm font-medium text-center rounded-lg py-2.5 transition border border-[#2d7a4f] text-[#2d7a4f] hover:bg-[#e8f5e9]"
                >
                  {tier.cta}
                </Link>
              ) : (
                <button
                  type="button"
                  disabled={!!loading}
                  onClick={() => handleChangePlan(tier.id)}
                  className={`text-sm font-medium text-center rounded-lg py-2.5 transition disabled:opacity-50 disabled:cursor-not-allowed ${
                    isHighlighted
                      ? 'bg-[#2d7a4f] text-white hover:bg-[#235f3d]'
                      : 'border border-[#2d7a4f] text-[#2d7a4f] hover:bg-[#e8f5e9]'
                  }`}
                >
                  {loading === tier.id ? (isUpgrade ? t('upgrading') : t('switching')) : tier.cta}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-center flex flex-col gap-2">
        {user && (currentTier === 'pro' || currentTier === 'business') && (
          <>
            {showCancelConfirm ? (
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 flex flex-col gap-3 max-w-sm mx-auto">
                <p className="text-sm text-[#1a2e1f] font-medium">{t('cancelConfirmTitle')}</p>
                <p className="text-xs text-[#6b8c7a]">{t('cancelConfirmDesc')}</p>
                {error && <p className="text-xs text-red-600">{error}</p>}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowCancelConfirm(false); setError(''); }}
                    className="flex-1 text-sm border border-[#cdd8cc] text-[#6b8c7a] rounded-lg py-2 hover:bg-[#f4f7f4] transition"
                  >
                    {t('keepPlan')}
                  </button>
                  <button
                    type="button"
                    disabled={loading === 'cancel'}
                    onClick={handleCancel}
                    className="flex-1 text-sm bg-red-500 text-white rounded-lg py-2 hover:bg-red-600 transition disabled:opacity-50"
                  >
                    {loading === 'cancel' ? t('cancelling') : t('yesCancel')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowCancelConfirm(true)}
                className="text-xs text-[#6b8c7a] hover:text-red-500 hover:underline transition"
              >
                {t('cancelSubscription')}              </button>
            )}
          </>
        )}
        <p className="text-xs text-[#9db8a5]">
          {t('securePayments')}
        </p>
        {user && (
          <Link href="/profile/" className="text-xs text-[#2d7a4f] hover:underline">
            {t('backToProfile')}
          </Link>
        )}
      </div>
    </main>
  );
}
